import { isConfiguracionHubPath } from "./config/configuracionRoutes";
import { isEquipoBasePath } from "./equipo/equipoRoutes";
import { isDatosMaestrosAutonomousPath } from "./datosMaestros/datosMaestrosNavigation";
import {
  isDespachoCommercialPath,
  isLaboralCommercialPath,
  isMaterialidadCommercialPath,
  isTramitesCommercialPath,
  isVisorCommercialPath,
} from "./commercialModuleRoutePaths";
import { isBuyerModuleVisibleToUser } from "@/lib/buyerModuleVisibility";

const WORK_MODULE = {
  VISOR_SAT: "visor_sat",
  ASISTENTE_LABORAL: "asistente_laboral",
  TRAMITES_FISCALES: "tramites_fiscales",
  GESTION_DESPACHO: "gestion_despacho",
  MATERIALIDAD: "materialidad",
  ANALISIS_FINANCIERO: "analisis_financiero",
};

const ENTITLEMENT_BY_WORK_MODULE = {
  [WORK_MODULE.VISOR_SAT]: "visor",
  [WORK_MODULE.ASISTENTE_LABORAL]: "laboral",
  [WORK_MODULE.TRAMITES_FISCALES]: "tramites",
  [WORK_MODULE.GESTION_DESPACHO]: "despacho",
  [WORK_MODULE.MATERIALIDAD]: "tramites",
};

/** Rutas transversales exentas del guard de entitlement (además de hubs admin). */
export const ROUTE_ENTITLEMENT_EXTRA_EXEMPT = new Set([
  "/registro-inicial",
  "/recuperacion-acceso",
  "/recuperacion-extravio",
  "/datos-maestros",
  "/datos-maestros/validador-check-id",
  "/datos-maestros/validacion-rfc-sat",
  "/gestion-datos",
  "/exportacion-local",
  "/gestionar-xml",
]);

const HUB_ADMIN_PATHS = new Set([
  "/ayuda",
  "/catalogo-compradores",
  "/cartera-licencias",
  "/usuarios-distribuidor",
  "/disenador/inicio",
  "/disenador/compradores-licencias",
  "/disenador/solicitudes-compradores",
  "/disenador/herramientas",
  "/disenador/licencias",
  "/disenador/modulos-comprador",
  "/disenador/centro-bnt",
]);

function normalizePath(pathname) {
  return String(pathname || "").split("?")[0];
}

function isPlatformRoute(path) {
  return path === "/platform" || path.startsWith("/platform/");
}

function isRouteEntitlementHubExempt(path, search = "") {
  if (!path || path === "/") return true;
  if (isConfiguracionHubPath(path)) return true;
  if (isEquipoBasePath(path)) return true;
  if (HUB_ADMIN_PATHS.has(path)) return true;
  if (ROUTE_ENTITLEMENT_EXTRA_EXEMPT.has(path)) return true;
  if (isDatosMaestrosAutonomousPath(path, search)) return true;
  return false;
}

/**
 * Mirror de resolveHubModuleAccess / panelHubEntry — sin imports con cadena @/ en tests.
 */
function isCommercialModuleEntitled(workModuleId, accessCtx = {}) {
  if (accessCtx.isPlatformDesigner) return true;
  const ent = accessCtx.entitlements || {};
  const visor = accessCtx.modulo_visor_sat;
  const laboral = accessCtx.modulo_asistente_laboral;
  const tramites = accessCtx.modulo_tramites_fiscales;
  const despacho = accessCtx.modulo_gestion_despacho;

  if (workModuleId === WORK_MODULE.VISOR_SAT) {
    if ("visor" in ent) return Boolean(ent.visor);
    return visor !== false;
  }
  if (workModuleId === WORK_MODULE.ASISTENTE_LABORAL) {
    if ("laboral" in ent) return Boolean(ent.laboral);
    return Boolean(laboral);
  }
  if (workModuleId === WORK_MODULE.TRAMITES_FISCALES) {
    if ("tramites" in ent) return Boolean(ent.tramites);
    return Boolean(tramites ?? visor ?? true);
  }
  if (workModuleId === WORK_MODULE.GESTION_DESPACHO) {
    if ("despacho" in ent) return Boolean(ent.despacho);
    return Boolean(despacho);
  }
  if (workModuleId === WORK_MODULE.MATERIALIDAD) {
    if ("tramites" in ent) return Boolean(ent.tramites);
    return Boolean(tramites ?? visor ?? true);
  }
  // Análisis financiero es general: no depende de licencia ni de trámites.
  if (workModuleId === WORK_MODULE.ANALISIS_FINANCIERO) return true;
  return true;
}

function hasAnyClientCatalogEntitlement(accessCtx = {}) {
  return (
    isCommercialModuleEntitled(WORK_MODULE.VISOR_SAT, accessCtx)
    || isCommercialModuleEntitled(WORK_MODULE.ASISTENTE_LABORAL, accessCtx)
    || isCommercialModuleEntitled(WORK_MODULE.TRAMITES_FISCALES, accessCtx)
    || isCommercialModuleEntitled(WORK_MODULE.GESTION_DESPACHO, accessCtx)
  );
}

/**
 * @returns {string|null} null si la ruta no exige entitlement de módulo comercial.
 */
export function resolveRouteEntitlementWorkModule(pathname, activeWorkModule, search = "") {
  const path = normalizePath(pathname);

  if (isRouteEntitlementHubExempt(path, search)) return null;
  if (isPlatformRoute(path)) return null;

  if (path === "/analisis-financiero" || path.startsWith("/analisis-financiero/")) {
    return WORK_MODULE.ANALISIS_FINANCIERO;
  }

  if (isMaterialidadCommercialPath(path)) {
    return WORK_MODULE.MATERIALIDAD;
  }

  if (isTramitesCommercialPath(path)) {
    return WORK_MODULE.TRAMITES_FISCALES;
  }

  if (isDespachoCommercialPath(path)) {
    return WORK_MODULE.GESTION_DESPACHO;
  }

  if (path === "/clientes") {
    if (activeWorkModule === WORK_MODULE.ASISTENTE_LABORAL) {
      return WORK_MODULE.ASISTENTE_LABORAL;
    }
    if (activeWorkModule === WORK_MODULE.TRAMITES_FISCALES) {
      return WORK_MODULE.TRAMITES_FISCALES;
    }
    if (activeWorkModule === WORK_MODULE.GESTION_DESPACHO) {
      return WORK_MODULE.GESTION_DESPACHO;
    }
    if (activeWorkModule === WORK_MODULE.MATERIALIDAD) {
      return WORK_MODULE.MATERIALIDAD;
    }
    return WORK_MODULE.VISOR_SAT;
  }

  if (isLaboralCommercialPath(path)) {
    return WORK_MODULE.ASISTENTE_LABORAL;
  }

  if (isVisorCommercialPath(path)) {
    return WORK_MODULE.VISOR_SAT;
  }

  return null;
}

/**
 * Guard Fase 3F-B — entitlement comercial requerido para deep links de módulos principales.
 *
 * @param {string} pathname
 * @param {object} accessCtx
 * @param {boolean} [accessCtx.isPlatformDesigner]
 * @param {boolean} [accessCtx.permLoading]
 * @param {string} [accessCtx.activeWorkModule]
 * @returns {{ allowed: boolean, reason?: 'loading' | 'no_entitlement' | 'exempt', workModuleId?: string|null, entitlementId?: string|null }}
 */
export function requireModuleEntitlementForRoute(pathname, accessCtx = {}) {
  const {
    isPlatformDesigner = false,
    buyerPreviewActive = false,
    permLoading = false,
    activeWorkModule,
    routeSearch = "",
    buyerModuleVisibility,
  } = accessCtx;

  if (isPlatformDesigner && !buyerPreviewActive) {
    return { allowed: true, reason: "exempt", workModuleId: null };
  }

  const path = normalizePath(pathname);
  const workModuleId = resolveRouteEntitlementWorkModule(path, activeWorkModule, routeSearch);

  if (!workModuleId) {
    return { allowed: true, reason: "exempt", workModuleId: null };
  }

  const entitlementId = ENTITLEMENT_BY_WORK_MODULE[workModuleId] || null;

  if (
    workModuleId
    && !isBuyerModuleVisibleToUser(workModuleId, {
      modules: buyerModuleVisibility,
      isPlatformDesigner,
      buyerPreviewActive,
    })
  ) {
    return {
      allowed: false,
      reason: "no_entitlement",
      workModuleId,
      entitlementId,
    };
  }

  if (permLoading) {
    return {
      allowed: false,
      reason: "loading",
      workModuleId,
      entitlementId,
    };
  }

  if (path === "/clientes" && hasAnyClientCatalogEntitlement(accessCtx)) {
    return {
      allowed: true,
      reason: "exempt",
      workModuleId: null,
      entitlementId: null,
    };
  }

  if (isCommercialModuleEntitled(workModuleId, accessCtx)) {
    return {
      allowed: true,
      workModuleId,
      entitlementId,
    };
  }

  return {
    allowed: false,
    reason: "no_entitlement",
    workModuleId,
    entitlementId,
  };
}
